import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import Select from "react-select";
import { Col, Form } from "react-bootstrap";

import { currency as formatCurrency } from "../../utils/number";
import { format as formatDate } from "../../utils/date";
import { EDIT_INSTANCE } from "../../constants/permissions";
import DataPanel from "../../layout/data-panel";
import Table from "../../layout/table";
import { orders as listOrders } from "../../actions/products";

const STATUS_OPTIONS = [
    { value: "building", label: "Building" },
    { value: "pending", label: "Pending" },
    { value: "processing", label: "Processing" },
    { value: "complete", label: "Complete" },
    { value: "applied", label: "Applied" },
    { value: "error", label: "Error" }
];

class Orders extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            page: 0,
            size: 25,
            search: "",
            order: "Product.Code.description",
            index: 0,
            direction: "ASC",
            statuses: ["building", "pending", "processing", "error"],
            data: [],
            pages: 0,
            total: 0,
            loading: true,
            error: "",
            view: "table",
        };
    }

    componentDidMount = () => {
        this.fetchData();
    }

    componentDidUpdate = (oldProps) => {
        const oldPermissions = (oldProps.auth?.permissions || []).join("|");
        const newPermissions = (this.props.auth?.permissions || []).join("|");
        if (oldPermissions !== newPermissions && this.canViewOrders && this.state.data.length === 0) {
            this.fetchData();
        }
    }

    permitted = (named) => (this.props.auth?.permissions || []).includes(named);

    get canViewOrders() {
        return this.permitted(EDIT_INSTANCE);
    }

    get headers() {
        return [
            {
                header: "Product",
                sortable: true,
                source: "Product.Code.description",
                className: "text-muted text-small text-uppercase w-25"
            },
            {
                header: "Key",
                sortable: true,
                source: "LicenseeProduct.key",
                className: "text-muted text-small text-uppercase w-20",
                onRender: this.onLinkFormat
            },
            {
                header: "Charged",
                sortable: true,
                source: "charge",
                className: "text-muted text-small text-uppercase w-10 text-end",
                onRender: this.onCurrencyFormat
            },
            {
                header: "Renews At",
                sortable: true,
                source: "price",
                className: "text-muted text-small text-uppercase w-10 text-end",
                onFormat: this.onPriceFormat
            },
            {
                header: "Changed",
                sortable: true,
                source: "changeDate",
                className: "text-muted text-small text-uppercase w-15",
                onFormat: this.onDateFormat
            },
            {
                header: "Status",
                sortable: true,
                source: "status",
                className: "text-muted text-small text-uppercase w-10"
            },
            {
                header: "Sync",
                sortable: true,
                source: "synchronization",
                className: "text-muted text-small text-uppercase w-10"
            }
        ];
    }

    fetchData = async () => {
        if (!this.canViewOrders) return;

        this.setState({ error: "", loading: true });
        const result = await this.props.listOrders({
            page: this.state.page,
            size: this.state.size,
            order: this.state.order,
            direction: this.state.direction,
            raw: 1,
            search: this.state.search,
            statuses: this.state.statuses
        });

        const data = Array.isArray(result) ? result : [];
        const total = Number(data[0]?.count || 0);
        this.setState({
            data,
            error: Array.isArray(result) ? "" : "Orders could not be loaded.",
            loading: false,
            pages: total ? Math.ceil(total / Number(this.state.size)) : 0,
            total,
        });
    }

    formatFrequencyCode = (e) => {
        switch (e) {
            case "P":
                return "Forever";
            case "M":
                return "Monthly";
            case "A":
                return "Annually";
            default:
                return "Unknown";
        }
    }

    onPriceFormat = (e, row) => {
        return `${Number(e) !== 0 ? formatCurrency(Number(e)) : "Free"} ${this.formatFrequencyCode(row.frequency?.toString())}`;
    }

    onCurrencyFormat = (e) => formatCurrency(Number(e));

    onDateFormat = (e) => formatDate(e, true);

    onLinkFormat = (e, row) => {
        const uuid = row.licenseeProductUuid || row.uuid;
        return uuid ? <a href={`/dashboard/products/${uuid.toString()}`}>{e || ""}</a> : (e || "");
    }

    onColumnClick = (e) => {
        const headers = this.headers;
        const index = headers.findIndex((item) => item.header === e.header && item.source === e.source);

        this.setState({
            index,
            direction: this.state.index === index && this.state.direction === "DESC" ? "ASC" : "DESC",
            order: headers[index].source,
            page: 0
        }, this.fetchData);
    }

    onSearch = (e) => {
        this.setState({
            search: e,
            page: 0
        }, this.fetchData);
    }

    onSize = (e) => {
        this.setState({
            page: 0,
            size: Number(e)
        }, this.fetchData);
    }

    onPageChange = (e) => {
        this.setState({
            page: Number(e)
        }, this.fetchData);
    }

    onStatusesChange = (options) => {
        this.setState({
            statuses: (options || []).map((option) => option.value),
            page: 0
        }, this.fetchData);
    }

    onViewChange = (view) => {
        this.setState({ view });
    }

    statusClass = (status) => {
        if (["complete", "applied"].includes(status)) return "is-success";
        if (["building", "pending", "processing"].includes(status)) return "is-warning";
        if (status === "error") return "is-danger";
        return "";
    }

    get statusFilter() {
        return (
            <div className="licensee-field licensee-orders-filter">
                <Form.Label>STATUS</Form.Label>
                <Select
                    value={STATUS_OPTIONS.filter((option) => this.state.statuses.includes(option.value))}
                    onChange={this.onStatusesChange}
                    classNamePrefix="react-select"
                    options={STATUS_OPTIONS}
                    placeholder="All Statuses"
                    isMulti
                    isClearable
                />
            </div>
        );
    }

    renderOrderCard = (order) => {
        const uuid = order.licenseeProductUuid || order.uuid;
        return (
            <article className="licensee-data-card" key={order.uuid}>
                <div className="licensee-data-card-title">
                    <strong>{order["Product.Code.description"] || "Product order"}</strong>
                    <span className={`licensee-status-pill ${this.statusClass(order.status)}`}>
                        {order.status || "Unknown"}
                    </span>
                </div>
                <div className="licensee-data-card-detail">
                    <span>Key</span>
                    <strong>
                        {uuid
                            ? <a href={`/dashboard/products/${uuid}`}>{order["LicenseeProduct.key"] || "View product"}</a>
                            : (order["LicenseeProduct.key"] || "Not assigned")}
                    </strong>
                </div>
                <div className="licensee-data-card-detail">
                    <span>Charged</span>
                    <strong>{this.onCurrencyFormat(order.charge)}</strong>
                </div>
                <div className="licensee-data-card-detail">
                    <span>Renews</span>
                    <strong>{this.onPriceFormat(order.price, order)}</strong>
                </div>
                <div className="licensee-data-card-detail">
                    <span>Changed</span>
                    <strong>{this.onDateFormat(order.changeDate)}</strong>
                </div>
            </article>
        );
    }

    render() {
        if (!this.canViewOrders) {
            return <Col xs="12">Orders not available.</Col>;
        }

        return (
            <Col xs="12">
                <DataPanel
                    icon="file-data"
                    label="Orders in view"
                    value={this.state.total}
                    description={`Filtered by ${this.state.statuses.length || "all"} statuses`}
                    view={this.state.view}
                    onViewChange={this.onViewChange}
                >
                    <Table
                        ariaLabel="Orders"
                        headers={this.headers}
                        page={this.state.data}
                        sort={{ index: this.state.index, direction: this.state.direction }}
                        size={this.state.size}
                        index={this.state.page}
                        pages={this.state.pages}
                        total={this.state.total}
                        search={this.state.search}
                        filter={this.statusFilter}
                        loading={this.state.loading}
                        emptyTitle={this.state.error || "No matching orders"}
                        emptyDescription={this.state.error ? "Try refreshing this page." : "Change the status filter to see other orders."}
                        variant="licensee"
                        view={this.state.view}
                        renderCard={this.renderOrderCard}
                        onColumnClick={this.onColumnClick}
                        onPageChange={this.onPageChange}
                        onSearch={this.onSearch}
                        onSize={this.onSize}
                        searchable
                        sizable
                    />
                </DataPanel>
            </Col>
        );
    }
}

Orders.propTypes = {
    auth: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
    auth: state.auth,
});

export { Orders };
export default connect(mapStateToProps, { listOrders })(Orders);
